import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FaArrowRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa'

const PREVIEW_WIDTHS = {
  DESKTOP: 1440,
  MOBILE: 390
}

const clampPosition = value => Math.max(0, Math.min(100, value))
const clampZoom = value => Math.max(1, Math.min(2.5, Number(value) || 1))

const THEMES = {
  MULTI_YELLOW: {
    shell: 'bg-[#fff3ad] border-[#212121]',
    title: 'bg-[#212121] text-[#ffe369]',
    accent: 'bg-[#ffe369]',
    eyebrow: 'text-[#6f5a00]',
    button: 'bg-[#212121] text-white',
    cardImage: 'bg-[linear-gradient(145deg,#ffffff_0%,#fff4b8_100%)]',
    cardAction: 'bg-[#ffe369] text-[#212121]'
  },
  ELECTRIC_BLUE: {
    shell: 'bg-[#e0efff] border-[#183153]',
    title: 'bg-[#183153] text-[#b9dcff]',
    accent: 'bg-[#62a9f9]',
    eyebrow: 'text-[#285f9d]',
    button: 'bg-[#183153] text-white',
    cardImage: 'bg-[linear-gradient(145deg,#ffffff_0%,#dceeff_100%)]',
    cardAction: 'bg-[#b9dcff] text-[#183153]'
  },
  CORAL_POP: {
    shell: 'bg-[#ffe1da] border-[#76283d]',
    title: 'bg-[#76283d] text-[#ffd7cb]',
    accent: 'bg-[#ff806d]',
    eyebrow: 'text-[#9b3e4e]',
    button: 'bg-[#76283d] text-white',
    cardImage: 'bg-[linear-gradient(145deg,#ffffff_0%,#ffe1da_100%)]',
    cardAction: 'bg-[#ffd7cb] text-[#76283d]'
  }
}

const normalizeHexColor = value => {
  if (!/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(value || '')) return '#7A263A'
  const color = value.toUpperCase()
  return color.length === 4
    ? `#${color.slice(1).split('').map(character => character + character).join('')}`
    : color
}

const mixColors = (base, target, amount) => {
  const source = normalizeHexColor(base)
  const destination = normalizeHexColor(target)
  const channels = [1, 3, 5].map(index => Math.round(
    parseInt(source.slice(index, index + 2), 16) * (1 - amount) + parseInt(destination.slice(index, index + 2), 16) * amount
  ).toString(16).padStart(2, '0'))
  return `#${channels.join('')}`.toUpperCase()
}

const readableText = color => {
  const [red, green, blue] = [1, 3, 5].map(index => parseInt(normalizeHexColor(color).slice(index, index + 2), 16) / 255)
  const luminance = [red, green, blue].reduce((total, channel, index) => total + [0.2126, 0.7152, 0.0722][index] * (channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2), 0)
  return luminance > 0.35 ? '#212121' : '#FFFFFF'
}

const getTheme = (themeName, customThemeColor) => {
  // Preset palettes always win. This also keeps legacy records with a stale
  // customThemeColor from rendering as a custom palette.
  if (themeName !== 'CUSTOM') return THEMES[themeName] || THEMES.MULTI_YELLOW

  const shell = normalizeHexColor(customThemeColor)
  const title = mixColors(shell, '#000000', 0.56)
  const accent = mixColors(shell, '#FFFFFF', 0.34)
  return {
    shell: 'bg-[var(--highlight-shell)] border-[var(--highlight-border)]',
    title: 'bg-[var(--highlight-title)] text-[var(--highlight-title-text)]',
    accent: 'bg-[var(--highlight-accent)]',
    eyebrow: 'text-[var(--highlight-eyebrow)]',
    button: 'bg-[var(--highlight-title)] text-[var(--highlight-title-text)]',
    cardImage: 'bg-[var(--highlight-card)]',
    cardAction: 'bg-[var(--highlight-accent)] text-[var(--highlight-action-text)]',
    style: {
      '--highlight-shell': shell,
      '--highlight-border': title,
      '--highlight-title': title,
      '--highlight-title-text': readableText(title),
      '--highlight-accent': accent,
      '--highlight-eyebrow': title,
      '--highlight-card': mixColors(shell, '#FFFFFF', 0.74),
      '--highlight-action-text': readableText(accent)
    }
  }
}

const PLACEHOLDER_ITEMS = [
  { attributeId: 'preview-1', name: 'First card' },
  { attributeId: 'preview-2', name: 'Second card' },
  { attributeId: 'preview-3', name: 'Third card' }
]

const HighlightCard = ({ item, theme, viewport, mosaic = false }) => {
  const isDesktop = viewport === 'DESKTOP'

  return (
    <article
      className={`highlight-motion group box-border flex flex-col overflow-hidden rounded-[22px] border-2 border-white/80 bg-white/90 shadow-[0_10px_26px_rgba(33,33,33,0.12)] transition duration-300 hover:-translate-y-1.5 hover:border-white hover:shadow-[0_18px_36px_rgba(33,33,33,0.20)] ${isDesktop ? `w-full ${mosaic ? 'h-full' : 'h-[280px]'}` : 'h-[252px] w-36 shrink-0 snap-start'}`}
    >
      <div className={`relative min-h-0 flex-1 overflow-hidden ${theme.cardImage}`}>
        <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full border-2 border-black/[0.045] bg-white/25" />
        <div className="absolute -bottom-14 -left-10 h-28 w-28 rotate-12 rounded-[32px] border-2 border-black/[0.035]" />
        {item.image?.url
          ? (
            <img
              src={item.image.url}
              alt=""
              className={`highlight-motion relative z-[1] h-full w-full object-contain transition duration-500 group-hover:scale-[1.06] ${isDesktop ? 'p-3' : 'p-2'}`}
            />
          )
          : (
            <div className="relative z-[1] flex h-full items-center justify-center px-4 text-center text-xs font-bold uppercase tracking-[0.12em] text-black/30">
              Image preview
            </div>
          )}
      </div>
      <div className="relative flex min-h-[72px] shrink-0 items-center gap-2 border-t-2 border-black/5 bg-white/95 px-3 py-3">
        <div className={`absolute inset-x-0 top-0 h-1 ${theme.accent}`} />
        <span className="line-clamp-2 min-w-0 flex-1 break-words text-[13px] font-bold leading-tight text-[#212121]">{item.title || item.name}</span>
        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition duration-300 group-hover:bg-[#212121] group-hover:text-white ${theme.cardAction}`}>
          <FaArrowRight className="highlight-motion text-xs transition duration-300 group-hover:translate-x-0.5" />
        </span>
      </div>
    </article>
  )
}

const RailControls = ({ visible, canPrevious, canNext, onPrevious, onNext, theme }) => {
  if (!visible) return null

  const controlClass = `absolute top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full text-white shadow-lg ring-2 ring-white/70 transition hover:scale-105 focus:outline-none focus:ring-4 focus:ring-white disabled:cursor-default disabled:opacity-40 disabled:hover:scale-100 ${theme.button}`

  return (
    <>
      <button type="button" aria-label="Previous highlight cards" disabled={!canPrevious} onClick={onPrevious} className={`left-2 ${controlClass}`}>
        <FaChevronLeft className="text-white" />
      </button>
      <button type="button" aria-label="Next highlight cards" disabled={!canNext} onClick={onNext} className={`right-2 ${controlClass}`}>
        <FaChevronRight className="text-white" />
      </button>
    </>
  )
}

const SignatureTitleCard = ({ section, theme, viewport, className }) => (
  <div className={`relative box-border flex flex-col justify-end overflow-hidden rounded-[22px] border-2 border-white/20 shadow-[0_12px_28px_rgba(33,33,33,0.18)] ${theme.title} ${className}`}>
    <div className={`absolute -right-10 -top-10 h-32 w-32 rounded-full opacity-25 ${theme.accent}`} />
    <div className="absolute -bottom-20 -left-16 h-36 w-36 rounded-full border-[18px] border-white/10" />
    <div className="absolute left-5 right-5 top-5 h-px bg-white/20" />
    <div className={`absolute right-5 top-5 h-2.5 w-2.5 rotate-45 ${theme.accent}`} />
    <h3 className={`relative z-[1] font-black leading-tight ${viewport === 'DESKTOP' ? 'text-2xl' : 'text-xl'}`}>
      {section.highlightTitle || section.title}
    </h3>
  </div>
)

const SignatureRail = ({ section, theme, viewport }) => {
  const railRef = useRef(null)
  const [controls, setControls] = useState({ visible: false, canPrevious: false, canNext: false })
  const isDesktop = viewport === 'DESKTOP'

  const updateControls = useCallback(() => {
    const rail = railRef.current
    if (!rail) return
    const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth)
    setControls({
      visible: maxScroll > 2,
      canPrevious: rail.scrollLeft > 2,
      canNext: rail.scrollLeft < maxScroll - 2
    })
  }, [])

  useEffect(() => {
    if (!isDesktop) return undefined
    const frame = window.requestAnimationFrame(updateControls)
    window.addEventListener('resize', updateControls)
    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', updateControls)
    }
  }, [isDesktop, section.items.length, updateControls])

  const move = direction => railRef.current?.scrollBy({
    left: direction * Math.max(280, railRef.current.clientWidth * 0.75),
    behavior: 'smooth'
  })

  if (!isDesktop) {
    return (
      <div className="highlight-scrollbar flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2">
        <SignatureTitleCard section={section} theme={theme} viewport={viewport} className="h-[252px] w-40 shrink-0 snap-start p-5" />
        {section.items.map(item => <HighlightCard key={item.cardId || item.attributeId} item={item} theme={theme} viewport={viewport} />)}
      </div>
    )
  }

  return (
    <div className="grid h-[300px] grid-cols-[220px_minmax(0,1fr)] items-center gap-5">
      <SignatureTitleCard section={section} theme={theme} viewport={viewport} className="h-[280px] p-6" />
      <div className="relative h-full min-w-0">
        <div ref={railRef} onScroll={updateControls} className="highlight-scrollbar grid h-full snap-x snap-mandatory grid-flow-col auto-cols-[calc((100%_-_5rem)/6)] items-center gap-4 overflow-x-auto">
          {section.items.map(item => <HighlightCard key={item.cardId || item.attributeId} item={item} theme={theme} viewport={viewport} />)}
        </div>
        <RailControls
          visible={controls.visible}
          canPrevious={controls.canPrevious}
          canNext={controls.canNext}
          onPrevious={() => move(-1)}
          onNext={() => move(1)}
          theme={theme}
        />
      </div>
    </div>
  )
}

const EditorialTitleCard = ({ section, theme, viewport, className }) => {
  const isDesktop = viewport === 'DESKTOP'

  return (
    <div className={`relative box-border flex shrink-0 flex-col justify-end overflow-hidden rounded-[22px] border-2 border-white/20 shadow-[0_12px_28px_rgba(33,33,33,0.18)] ${isDesktop ? 'w-full p-6' : 'h-[252px] w-36 snap-start p-5'} ${theme.title} ${className}`}>
      <div className={`absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-30 ${theme.accent}`} />
      <div className="absolute inset-y-0 left-1/3 w-px bg-white/10" />
      <div className="absolute inset-y-0 left-2/3 w-px bg-white/10" />
      <div className="absolute inset-x-0 top-1/3 h-px bg-white/10" />
      <div className="absolute inset-x-0 top-2/3 h-px bg-white/10" />
      <div className="absolute -bottom-16 -left-12 h-32 w-32 rotate-12 rounded-[36px] border-[14px] border-white/10" />
      <div className={`absolute right-5 top-5 h-3 w-3 rotate-45 ${theme.accent}`} />
      <div className={`absolute right-10 top-10 h-1.5 w-1.5 rotate-45 opacity-70 ${theme.accent}`} />
      <div className={`absolute bottom-0 left-0 h-1.5 w-2/3 ${theme.accent}`} />
      <h3 className={`relative z-[1] line-clamp-3 max-w-[16rem] font-black leading-tight ${isDesktop ? 'text-2xl' : 'text-xl'}`}>
        {section.highlightTitle || section.title}
      </h3>
    </div>
  )
}

const SpotlightMosaic = ({ section, theme, viewport }) => {
  const isDesktop = viewport === 'DESKTOP'

  return (
    <div className={isDesktop
      ? 'grid grid-flow-dense grid-cols-4 auto-rows-[240px] gap-4 overflow-visible'
      : 'highlight-scrollbar flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2'}>
      {!isDesktop && (
        <EditorialTitleCard section={section} theme={theme} viewport={viewport} />
      )}
      {section.items.map((item, index) => (
        <React.Fragment key={item.cardId || item.attributeId}>
          <div className={isDesktop ? `h-full ${index === 0 ? 'col-span-2 row-span-2' : ''}` : 'shrink-0'}>
            <HighlightCard item={item} theme={theme} viewport={viewport} mosaic />
          </div>
          {isDesktop && index === 0 && (
            <EditorialTitleCard section={section} theme={theme} viewport={viewport} className="col-span-2 h-full" />
          )}
        </React.Fragment>
      ))}
    </div>
  )
}

const GalleryGrid = ({ section, theme, viewport }) => {
  const isDesktop = viewport === 'DESKTOP'

  return (
    <div className={isDesktop
      ? 'grid grid-cols-[repeat(auto-fill,170px)] justify-start gap-4 overflow-visible'
      : 'highlight-scrollbar flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2'}>
      <EditorialTitleCard section={section} theme={theme} viewport={viewport} className={isDesktop ? 'col-span-2 h-[280px]' : ''} />
      {section.items.map(item => <HighlightCard key={item.cardId || item.attributeId} item={item} theme={theme} viewport={viewport} />)}
    </div>
  )
}

const ScaledPreviewFrame = ({ viewport, children }) => {
  const hostRef = useRef(null)
  const canvasRef = useRef(null)
  const logicalWidth = PREVIEW_WIDTHS[viewport]
  const [frame, setFrame] = useState({ height: 0, left: 0, scale: 1 })

  const measure = useCallback(() => {
    if (!hostRef.current || !canvasRef.current) return
    const hostWidth = hostRef.current.clientWidth
    if (!hostWidth) return
    const scale = Math.min(1, hostWidth / logicalWidth)
    const height = canvasRef.current.offsetHeight * scale
    const left = Math.max(0, (hostWidth - logicalWidth * scale) / 2)
    setFrame(current => (
      Math.abs(current.height - height) < 0.5 &&
      Math.abs(current.left - left) < 0.5 &&
      Math.abs(current.scale - scale) < 0.001
        ? current
        : { height, left, scale }
    ))
  }, [logicalWidth])

  useEffect(() => {
    const frameId = window.requestAnimationFrame(measure)
    const observer = new ResizeObserver(measure)
    if (hostRef.current) observer.observe(hostRef.current)
    if (canvasRef.current) observer.observe(canvasRef.current)
    window.addEventListener('resize', measure)
    return () => {
      window.cancelAnimationFrame(frameId)
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [measure])

  return (
    <div
      ref={hostRef}
      className="relative w-full overflow-hidden"
      style={{ height: frame.height || (viewport === 'DESKTOP' ? 520 : 360) }}
      data-preview-viewport={viewport.toLowerCase()}
    >
      <div
        ref={canvasRef}
        className="absolute top-0"
        style={{
          left: frame.left,
          width: logicalWidth,
          transform: `scale(${frame.scale})`,
          transformOrigin: 'top left'
        }}
      >
        {children}
      </div>
    </div>
  )
}

const PreviewCanvas = ({ section, theme, viewport, positioning, onBackgroundPositionChange }) => {
  const dragRef = useRef(null)
  const isDesktop = viewport === 'DESKTOP'
  const position = section.backgroundImagePosition || { x: 50, y: 0 }
  const zoom = clampZoom(section.backgroundImageZoom)

  const updatePosition = nextPosition => onBackgroundPositionChange?.({
    x: clampPosition(nextPosition.x),
    y: clampPosition(nextPosition.y)
  })

  const startDrag = event => {
    if (!positioning || !section.backgroundImageUrl) return
    const rect = event.currentTarget.getBoundingClientRect()
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      width: rect.width,
      height: rect.height,
      position
    }
    event.currentTarget.setPointerCapture(event.pointerId)
    event.currentTarget.focus()
  }

  const moveImage = event => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    updatePosition({
      x: drag.position.x - ((event.clientX - drag.startX) / drag.width) * 100,
      y: drag.position.y - ((event.clientY - drag.startY) / drag.height) * 100
    })
  }

  const stopDrag = event => {
    if (dragRef.current?.pointerId !== event.pointerId) return
    dragRef.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  const nudgeImage = event => {
    const offsets = {
      ArrowLeft: { x: -5, y: 0 },
      ArrowRight: { x: 5, y: 0 },
      ArrowUp: { x: 0, y: -5 },
      ArrowDown: { x: 0, y: 5 }
    }
    const offset = offsets[event.key]
    if (!offset) return
    event.preventDefault()
    updatePosition({ x: position.x + offset.x, y: position.y + offset.y })
  }

  return (
    <section className={`highlight-preview w-full bg-white ${isDesktop ? 'py-6' : 'px-4 py-4'}`}>
      <div
        className={`relative mx-auto overflow-hidden rounded-[28px] border-2 shadow-[0_14px_40px_rgba(33,33,33,0.08)] ${isDesktop ? 'w-10/12 p-8' : 'w-full p-5'} ${theme.shell}`}
        style={theme.style}
      >
        {!!section.backgroundImageUrl && (
          <img
            src={section.backgroundImageUrl}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.42]"
            style={{
              objectPosition: `${position.x}% ${position.y}%`,
              transform: `scale(${zoom})`,
              transformOrigin: `${position.x}% ${position.y}%`,
              transition: positioning ? 'none' : 'transform 300ms ease',
              WebkitMaskImage: 'linear-gradient(to bottom, black 0%, rgba(0,0,0,0.82) 45%, rgba(0,0,0,0.55) 100%)',
              maskImage: 'linear-gradient(to bottom, black 0%, rgba(0,0,0,0.82) 45%, rgba(0,0,0,0.55) 100%)'
            }}
          />
        )}
        <div className={`absolute -right-16 -top-16 h-48 w-48 rounded-full opacity-15 ${theme.accent}`} />
        {!!(section.subtitle || section.title) && (
          <header className={`relative ${isDesktop ? 'mb-7' : 'mb-5'}`}>
            <div>
              {!!section.subtitle && (
                <div className={`mb-1 text-[11px] font-bold uppercase tracking-[0.22em] ${theme.eyebrow}`}>
                  {section.subtitle}
                </div>
              )}
              {!!section.title && <h2 className={`font-black leading-tight text-[#212121] ${isDesktop ? 'text-3xl' : 'text-2xl'}`}>{section.title}</h2>}
            </div>
          </header>
        )}

        <div className="relative">
          {section.layout === 'SIGNATURE_RAIL' && <SignatureRail section={section} theme={theme} viewport={viewport} />}
          {section.layout === 'SPOTLIGHT_MOSAIC' && <SpotlightMosaic section={section} theme={theme} viewport={viewport} />}
          {section.layout === 'GALLERY_GRID' && <GalleryGrid section={section} theme={theme} viewport={viewport} />}
        </div>

        {positioning && !!section.backgroundImageUrl && (
          <div
            role="application"
            tabIndex={0}
            aria-label="Background image position. Drag the preview or use arrow keys to adjust."
            className="absolute inset-0 z-30 touch-none select-none cursor-grab outline-none ring-4 ring-inset ring-white/80 active:cursor-grabbing focus:ring-[#212121]"
            onPointerDown={startDrag}
            onPointerMove={moveImage}
            onPointerUp={stopDrag}
            onPointerCancel={stopDrag}
            onKeyDown={nudgeImage}
            data-testid="background-position-surface"
          >
            <div className="pointer-events-none absolute left-4 top-4 rounded-full bg-black/75 px-4 py-2 text-sm font-semibold text-white shadow-lg">
              Drag to reposition
            </div>
            <div
              className="pointer-events-none absolute h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-black/30 shadow-[0_0_0_2px_rgba(0,0,0,0.45)]"
              style={{ left: `${position.x}%`, top: `${position.y}%` }}
            >
              <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-white" />
              <div className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-white" />
            </div>
            <div className="pointer-events-none absolute bottom-4 left-4 rounded-full bg-white/95 px-4 py-2 text-sm font-bold text-gray-800 shadow-lg">
              X {Math.round(position.x)}% · Y {Math.round(position.y)}%
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

const HighlightSectionPreview = ({
  title,
  subtitle,
  highlightTitle,
  layout,
  theme: themeName,
  customThemeColor,
  backgroundImage,
  items,
  viewport = 'DESKTOP',
  positioning = false,
  onBackgroundPositionChange
}) => {
  const theme = getTheme(themeName, customThemeColor)
  const normalizedViewport = viewport === 'MOBILE' ? 'MOBILE' : 'DESKTOP'
  const section = {
    title: title.trim(),
    subtitle: subtitle.trim(),
    highlightTitle: highlightTitle.trim() || title.trim() || 'Your feature title',
    backgroundImageUrl: backgroundImage?.url,
    backgroundImagePosition: backgroundImage?.position,
    backgroundImageZoom: backgroundImage?.zoom,
    customThemeColor,
    layout,
    items: items.length ? items : PLACEHOLDER_ITEMS
  }

  return (
    <ScaledPreviewFrame viewport={normalizedViewport}>
      <PreviewCanvas
        section={section}
        theme={theme}
        viewport={normalizedViewport}
        positioning={positioning}
        onBackgroundPositionChange={onBackgroundPositionChange}
      />
    </ScaledPreviewFrame>
  )
}

export default HighlightSectionPreview
